import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'
import { DEFAULT_USER_PREFERENCES, type UserPreferences } from 'types'

// When VITE_SKIP_AUTH=true, persist preferences to localStorage instead of the
// API so the Settings UI and saved table views are fully exercisable without a
// running backend (mirrors the dev bypass in useUserProfileQuery).
const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === 'true'
const LOCAL_KEY = 'vera:preferences'

export const USER_PREFERENCES_QUERY_KEY = ['user', 'preferences']

const readLocal = (): UserPreferences => {
  try {
    const raw = window.localStorage.getItem(LOCAL_KEY)
    if (!raw) return { ...DEFAULT_USER_PREFERENCES }
    return { ...DEFAULT_USER_PREFERENCES, ...(JSON.parse(raw) as UserPreferences) }
  } catch {
    return { ...DEFAULT_USER_PREFERENCES }
  }
}

const writeLocal = (prefs: UserPreferences) => {
  try {
    window.localStorage.setItem(LOCAL_KEY, JSON.stringify(prefs))
  } catch {
    /* ignore quota / unavailable storage */
  }
}

type PreferencesApiResponse = { preferences?: UserPreferences | null }

export const useUserPreferencesQuery = (options?: { enabled?: boolean }) => {
  const isEnabled = options?.enabled !== false

  const query = useQuery({
    queryKey: USER_PREFERENCES_QUERY_KEY,
    queryFn: async (): Promise<UserPreferences> => {
      if (SKIP_AUTH) return readLocal()
      const response = await createApiCall({
        method: 'GET',
        url: '/auth/admin/preferences',
      })
      const data = response.data as PreferencesApiResponse
      return { ...DEFAULT_USER_PREFERENCES, ...(data?.preferences ?? {}) }
    },
    enabled: isEnabled,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    placeholderData: previousData => previousData,
  })

  return {
    preferences: query.data ?? { ...DEFAULT_USER_PREFERENCES },
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  }
}

export const useUserPreferencesMutation = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation<UserPreferences, Error, Partial<UserPreferences>>({
    mutationFn: async partial => {
      if (SKIP_AUTH) {
        const next = { ...readLocal(), ...partial }
        writeLocal(next)
        return next
      }
      const response = await createApiCall({
        method: 'PUT',
        url: '/auth/admin/preferences',
        data: { preferences: partial },
      })
      const data = response.data as PreferencesApiResponse
      return { ...DEFAULT_USER_PREFERENCES, ...(data?.preferences ?? {}) }
    },
    // Merge the partial we just sent (`variables`) on TOP of both the existing
    // cache and the API echo. The API doesn't always return the full preferences
    // object; trusting only `next` could collapse it back to defaults and drop a
    // just-saved view from the list. Applying `variables` last makes the change we
    // made authoritative, so a new saved view appears immediately.
    onSuccess: (next, variables) => {
      queryClient.setQueryData<UserPreferences>(USER_PREFERENCES_QUERY_KEY, old => ({
        ...DEFAULT_USER_PREFERENCES,
        ...(old ?? {}),
        ...next,
        ...variables,
      }))
    },
  })

  const update = useCallback(
    (partial: Partial<UserPreferences>) => mutation.mutateAsync(partial),
    [mutation],
  )

  return {
    update,
    isSaving: mutation.isPending,
    isError: mutation.isError,
  }
}
